import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Table, Typography, Tag, Space, Spin } from 'antd';
import {
  KeyOutlined,
  ShopOutlined,
  PhoneOutlined,
  ProjectOutlined,
  ArrowUpOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';

const { Title, Text } = Typography;

const Dashboard = () => {
  const [stats, setStats] = useState({ kiralik: 0, satilik: 0, aramalar: 0, projeSatis: 0 });
  const [recentAramalar, setRecentAramalar] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [kiralikRes, satilikRes, aramalarRes, projeSatisRes] = await Promise.allSettled([
        api.get('/kiralik'),
        api.get('/satilik'),
        api.get('/aramalar'),
        api.get('/projesatis'),
      ]);

      const kiralikData = kiralikRes.status === 'fulfilled' ? kiralikRes.value.data : [];
      const satilikData = satilikRes.status === 'fulfilled' ? satilikRes.value.data : [];
      const aramalarData = aramalarRes.status === 'fulfilled' ? aramalarRes.value.data : [];
      const projeSatisData = projeSatisRes.status === 'fulfilled' ? projeSatisRes.value.data : [];

      const kiralikArr = Array.isArray(kiralikData) ? kiralikData : kiralikData.data || [];
      const satilikArr = Array.isArray(satilikData) ? satilikData : satilikData.data || [];
      const aramalarArr = Array.isArray(aramalarData) ? aramalarData : aramalarData.data || [];
      const projeSatisArr = Array.isArray(projeSatisData) ? projeSatisData : projeSatisData.data || [];

      setStats({
        kiralik: kiralikArr.length,
        satilik: satilikArr.length,
        aramalar: aramalarArr.length,
        projeSatis: projeSatisArr.length,
      });

      setRecentAramalar(aramalarArr.slice(0, 5));
    } catch (err) {
      console.error('Dashboard data fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const aramaColumns = [
    {
      title: 'Arayan Adı',
      dataIndex: 'arayanAdi',
      key: 'arayanAdi',
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: 'Telefon',
      dataIndex: 'telefon',
      key: 'telefon',
    },
    {
      title: 'Arama Sebebi',
      dataIndex: 'aramaSebebi',
      key: 'aramaSebebi',
      ellipsis: true,
    },
    {
      title: 'Tarih',
      dataIndex: 'tarih',
      key: 'tarih',
      render: (val) => val ? dayjs(val).format('DD.MM.YYYY') : '-',
    },
    {
      title: 'Kaydeden',
      dataIndex: 'kaydeden',
      key: 'kaydeden',
    },
  ];

  const statCards = [
    {
      title: 'Kiralık İşlemler',
      value: stats.kiralik,
      icon: <KeyOutlined style={{ fontSize: 28, color: '#1677ff' }} />,
      color: '#e6f4ff',
      borderColor: '#91caff',
    },
    {
      title: 'Satılık İşlemler',
      value: stats.satilik,
      icon: <ShopOutlined style={{ fontSize: 28, color: '#52c41a' }} />,
      color: '#f6ffed',
      borderColor: '#b7eb8f',
    },
    {
      title: 'Arama Kayıtları',
      value: stats.aramalar,
      icon: <PhoneOutlined style={{ fontSize: 28, color: '#fa8c16' }} />,
      color: '#fff7e6',
      borderColor: '#ffd591',
    },
    {
      title: 'Proje Satışları',
      value: stats.projeSatis,
      icon: <ProjectOutlined style={{ fontSize: 28, color: '#722ed1' }} />,
      color: '#f9f0ff',
      borderColor: '#d3adf7',
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0 }}>
          Hoş Geldiniz, {user?.adSoyad} 👋
        </Title>
        <Text type="secondary">
          {dayjs().format('DD MMMM YYYY, dddd')} — DekaPortal Yönetim Paneli
        </Text>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {statCards.map((stat) => (
          <Col xs={24} sm={12} lg={6} key={stat.title}>
            <Card
              className="stat-card"
              style={{
                background: stat.color,
                borderColor: stat.borderColor,
                borderRadius: 10,
              }}
              bodyStyle={{ padding: '20px 24px' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Statistic
                  title={<Text style={{ fontSize: 13, color: '#666' }}>{stat.title}</Text>}
                  value={stat.value}
                  valueStyle={{ fontSize: 32, fontWeight: 700 }}
                />
                <div
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    background: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  }}
                >
                  {stat.icon}
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Card
        className="content-card"
        title={
          <Space>
            <PhoneOutlined style={{ color: '#fa8c16' }} />
            <span>Son Arama Kayıtları</span>
          </Space>
        }
        extra={<a href="/aramalar">Tümünü Gör</a>}
      >
        <Table
          columns={aramaColumns}
          dataSource={recentAramalar}
          rowKey="id"
          pagination={false}
          size="small"
          locale={{ emptyText: 'Henüz arama kaydı bulunmuyor' }}
        />
      </Card>
    </div>
  );
};

export default Dashboard;
